import { useState } from 'react';
import './ContentPageEditor.css';

/**
 * Contact Page Editor Component
 *
 * Handles structured contact location data stored as JSON in the content field.
 * Format: { description: string, locations: [{id, title, mobile, email, address}] }
 */
function ContactPageEditor({ value, onChange }) {
  const [contactData, setContactData] = useState(() => {
    // Parse JSON from content field
    if (value && typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        console.error('Failed to parse contact data:', e);
        return { description: '', locations: [] };
      }
    }
    return { description: '', locations: [] };
  });

  const updateContactData = (newData) => {
    setContactData(newData);
    // Stringify and send to parent
    onChange(JSON.stringify(newData));
  };

  const handleDescriptionChange = (description) => {
    updateContactData({ ...contactData, description });
  };

  const handleLocationChange = (index, field, value) => {
    const newLocations = [...contactData.locations];
    newLocations[index] = { ...newLocations[index], [field]: value };
    updateContactData({ ...contactData, locations: newLocations });
  };

  const addLocation = () => {
    const newLocation = {
      id: String(Date.now()),
      title: '',
      mobile: '',
      email: '',
      address: '',
    };
    updateContactData({
      ...contactData,
      locations: [...contactData.locations, newLocation],
    });
  };

  const removeLocation = (index) => {
    const newLocations = contactData.locations.filter((_, i) => i !== index);
    updateContactData({ ...contactData, locations: newLocations });
  };

  return (
    <div className="contact-page-editor">
      {/* Description */}
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-textarea"
          placeholder="Enter contact page description..."
          value={contactData.description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          rows={3}
        />
        <span className="form-hint">Brief introduction for the contact page</span>
      </div>

      {/* Locations */}
      <div className="form-group">
        <div className="form-label-with-action">
          <label className="form-label">Contact Locations</label>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={addLocation}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{width: '16px', height: '16px'}}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Location
          </button>
        </div>

        {contactData.locations.length === 0 ? (
          <div className="empty-state-small">
            <p>No contact locations added yet.</p>
            <button type="button" className="btn btn-primary btn-sm" onClick={addLocation}>
              Add First Location
            </button>
          </div>
        ) : (
          <div className="locations-list">
            {contactData.locations.map((location, index) => (
              <div key={location.id} className="location-card">
                <div className="location-card-header">
                  <h4>Location {index + 1}</h4>
                  <button
                    type="button"
                    className="btn-icon danger"
                    onClick={() => removeLocation(index)}
                    title="Remove location"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                </div>

                <div className="location-fields">
                  <div className="form-group">
                    <label className="form-label">Location Title</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., United States, Australia"
                      value={location.title}
                      onChange={(e) => handleLocationChange(index, 'title', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Mobile Phone</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., +(44) 609 33 17 54"
                      value={location.mobile}
                      onChange={(e) => handleLocationChange(index, 'mobile', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="e.g., usa@touchline.com"
                      value={location.email}
                      onChange={(e) => handleLocationChange(index, 'email', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-textarea"
                      placeholder="e.g., 201 Oak Street 27 Manchester, UK"
                      value={location.address}
                      onChange={(e) => handleLocationChange(index, 'address', e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactPageEditor;
